/**
 * EditarPrefacturaModal - Modal para editar pedido de prefactura
 *
 * Diseño mejorado con:
 * - Panel izquierdo: Información del pedido (solo lectura)
 * - Panel derecho: Formulario de edición con inputs normales
 * - Cálculo automático de precio total
 */
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { Input } from '@shared/components/ui/input';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import { SectionBox } from '@shared/components/ui/SectionBox';
import {
  faFileInvoice,
  faInfoCircle,
  faLocationDot,
  faTruckFast,
  faCalculator,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCurrencyAmount, getCurrencySymbol, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { MovimientosIcon } from '@shared/components/ui/icons/warehouse.icons';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter/types';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCalculateLines } from '../../hooks/useCalculateLines';
import { useToast } from '@shared/hooks/use-toast';

export interface EditarPrefacturaModalRef {
  open: (data: EditarPrefacturaModalData) => void;
  close: () => void;
}

export interface EditarPrefacturaModalData {
  // Información general
  reference: string;
  shop: string;
  client: string;
  paymentMethod: string;
  internalId: string;
  shippingPrice: string;
  preparationPrice: string;
  codPrice: string;
  totalPrice: string;

  // Información logística
  type: string;
  logisticStatus: string;
  shippingOrReturn: string;
  courier: string;
  orderWeight: string;
  numberOfLines: string;

  // Origen
  originCountry: string;
  originMunicipality: string;
  originPostalCode: string;

  // Destino
  destinationCountry: string;
  destinationMunicipality: string;
  destinationPostalCode: string;

  onSave: (data: EditarPrefacturaModalData) => void;
}

// 🎯 Componente helper para campos de solo lectura (fuera del componente para evitar re-renders)
const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm text-complementary-text">{label}</span>
    <span className="text-sm text-text font-medium">{value || '-'}</span>
  </div>
);

// 🎯 Componente helper para campos del formulario (fuera del componente para evitar re-renders)
interface FormFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
  sanitize?: boolean;
}

const FormField = ({ label, value, onChange, type = 'text', placeholder = '', sanitize = false }: FormFieldProps) => {
  // 🎯 Función para sanitizar inputs de precio (solo 2 decimales)
  const sanitizePriceInput = (val: string): string => {
    let sanitized = val.replace('.', ',');
    sanitized = sanitized.replace(/[^0-9,]/g, '');
    const parts = sanitized.split(',');
    if (parts.length > 2) {
      sanitized = parts[0] + ',' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 2) {
      sanitized = parts[0] + ',' + parts[1].slice(0, 2);
    }
    return sanitized;
  };

  return (
    <div className="space-y-1">
      <label className="text-sm text-complementary-text">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => {
          const newValue = sanitize ? sanitizePriceInput(e.target.value) : e.target.value;
          onChange(newValue);
        }}
        placeholder={placeholder}
        className="h-9"
      />
    </div>
  );
};

export const EditarPrefacturaModal = forwardRef<EditarPrefacturaModalRef>((_, ref) => {
  const { t } = useTranslation('invoicesOrdersModule');
  const { t: tCommon } = useTranslation('common');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const currency = useCurrency(); // 🎯 Obtener moneda desde /me
  const { calculateLines, isCalculating } = useCalculateLines(); // 🎯 Hook para calcular líneas
  const { toast } = useToast(); // 🎯 Hook para mostrar toasts

  // Estado del modal
  const [modalData, setModalData] = useState<EditarPrefacturaModalData>({
    reference: '',
    shop: '',
    client: '',
    paymentMethod: '',
    internalId: '',
    shippingPrice: '',
    preparationPrice: '',
    codPrice: '',
    totalPrice: '',
    type: '',
    logisticStatus: '',
    shippingOrReturn: '',
    courier: '',
    orderWeight: '',
    numberOfLines: '',
    originCountry: '',
    originMunicipality: '',
    originPostalCode: '',
    destinationCountry: '',
    destinationMunicipality: '',
    destinationPostalCode: '',
    onSave: () => {},
  });

  // 🎯 Guardar datos originales para detectar cambios reales
  const [originalData, setOriginalData] = useState<EditarPrefacturaModalData | null>(null);

  // 🎯 Estado para trackear si hay cambios sin guardar
  const [hasChanges, setHasChanges] = useState(false);
  const [attemptingToClose, setAttemptingToClose] = useState(false);

  // 🎯 Ref para evitar que se muestre el modal de confirmación al guardar
  const isSavingRef = useRef(false);

  // 🎯 Estado del formulario editable
  const [formValues, setFormValues] = useState({
    shippingPrice: '',
    preparationPrice: '',
    codPrice: '',
    orderWeight: '',
    numberOfLines: '',
  });

  // 🎯 Manejar el intento de cerrar la modal
  const handleCloseAttempt = useCallback(() => {
    // Si estamos guardando, no mostrar el modal de confirmación
    if (isSavingRef.current) {
      isSavingRef.current = false;
      return;
    }

    if (hasChanges && !attemptingToClose) {
      // Si hay cambios y no estamos ya intentando cerrar, mostrar confirmación
      setAttemptingToClose(true);
      confirmModalRef.current?.open(
        () => {
          // Usuario confirmó: cerrar sin guardar
          setHasChanges(false);
          setAttemptingToClose(false);
          modalRef.current?.closeModal();
        },
        () => {
          // Usuario canceló: mantener modal abierta y reabrir
          setAttemptingToClose(false);
          // Reabrir la modal si se cerró
          setTimeout(() => {
            if (!modalRef.current) return;
            modalRef.current.openModal();
          }, 0);
        },
        t('modals.editar_prefactura.unsaved_changes.title', 'Cambios sin guardar'),
        t(
          'modals.editar_prefactura.unsaved_changes.message',
          '¿Estás seguro de que deseas cerrar? Los cambios realizados se perderán.',
        ),
        tCommon('modal.close', 'Cerrar sin guardar'),
        tCommon('modal.cancel', 'Cancelar'),
      );
    } else if (!hasChanges) {
      // No hay cambios, cerrar normalmente
      setAttemptingToClose(false);
      modalRef.current?.closeModal();
    }
  }, [hasChanges, attemptingToClose, t, tCommon]);

  useImperativeHandle(
    ref,
    () => ({
      open: (data: EditarPrefacturaModalData) => {
        setModalData(data);
        setOriginalData(data); // 🎯 Guardar copia de datos originales
        setHasChanges(false); // 🎯 Resetear estado de cambios
        setAttemptingToClose(false);
        // Inicializar valores del formulario (sin símbolo de moneda para inputs)
        const extractNumber = (value: string): string => {
          return value.replace(/[^\d,.-]/g, '').replace(',', '.');
        };
        setFormValues({
          shippingPrice: extractNumber(data.shippingPrice),
          preparationPrice: extractNumber(data.preparationPrice),
          codPrice: extractNumber(data.codPrice),
          orderWeight: extractNumber(data.orderWeight),
          numberOfLines: data.numberOfLines,
        });
        modalRef.current?.openModal();
      },
      close: () => {
        handleCloseAttempt();
      },
    }),
    [handleCloseAttempt],
  );

  // 🎯 Función helper para verificar si hay cambios comparando con los datos originales
  const checkForChanges = useCallback(() => {
    if (!originalData) {
      setHasChanges(false);
      return;
    }

    const extractNumber = (value: string): number => {
      return parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0;
    };

    const originalShipping = extractNumber(originalData.shippingPrice);
    const originalPreparation = extractNumber(originalData.preparationPrice);
    const originalCod = extractNumber(originalData.codPrice);
    const originalWeight = extractNumber(originalData.orderWeight);
    const originalNumItems = parseInt(originalData.numberOfLines, 10) || 0;

    const currentShipping = parseFloat(formValues.shippingPrice.replace(',', '.')) || 0;
    const currentPreparation = parseFloat(formValues.preparationPrice.replace(',', '.')) || 0;
    const currentCod = parseFloat(formValues.codPrice.replace(',', '.')) || 0;
    const currentWeight = parseFloat(formValues.orderWeight.replace(',', '.')) || 0;
    const currentNumItems = parseInt(formValues.numberOfLines, 10) || 0;

    const hasChanges =
      originalShipping !== currentShipping ||
      originalPreparation !== currentPreparation ||
      originalCod !== currentCod ||
      originalWeight !== currentWeight ||
      originalNumItems !== currentNumItems;

    setHasChanges(hasChanges);
  }, [formValues, originalData]);

  // Verificar cambios cuando cambian los valores del formulario
  useEffect(() => {
    checkForChanges();
  }, [formValues, checkForChanges]);

  const handleSave = () => {
    // Verificar que onSave existe y es una función
    if (!modalData.onSave || typeof modalData.onSave !== 'function') {
      console.error('onSave callback is not a function');
      return;
    }

    // Construir datos actualizados con valores formateados
    const formatPrice = (value: string): string => {
      const num = parseFloat(value.replace(',', '.')) || 0;
      return formatCurrencyAmount(num, currency);
    };

    const updatedData: EditarPrefacturaModalData = {
      ...modalData,
      shippingPrice: formatPrice(formValues.shippingPrice),
      preparationPrice: formatPrice(formValues.preparationPrice),
      codPrice: formatPrice(formValues.codPrice),
      orderWeight: formValues.orderWeight,
      numberOfLines: formValues.numberOfLines,
    };

    // 🎯 Marcar que estamos guardando para evitar el modal de confirmación
    isSavingRef.current = true;

    modalData.onSave(updatedData);
    setHasChanges(false);
    modalRef.current?.closeModal();
  };

  // 🎯 Función para recalcular líneas usando la API
  const handleRecalculate = useCallback(async () => {
    const orderId = parseInt(modalData.internalId, 10);
    if (!orderId || isNaN(orderId)) {
      return;
    }

    const weight = parseFloat(formValues.orderWeight.replace(',', '.')) || 0;
    const numItems = parseInt(formValues.numberOfLines, 10) || 0;

    if (weight <= 0 || numItems <= 0) {
      // Mostrar mensaje de error si faltan datos
      toast({
        title: t('modals.editar_prefactura.recalculate_error.title', 'Datos incompletos'),
        description: t(
          'modals.editar_prefactura.recalculate_error.message',
          'Por favor, ingresa un peso y número de líneas válidos para recalcular.',
        ),
        variant: 'destructive',
      });
      return;
    }

    const response = await calculateLines({
      order_id: orderId,
      weight: weight,
      num_items: numItems,
    });

    if (response) {
      // 🎯 Actualizar los valores del formulario con los valores calculados
      const formatPrice = (value: number): string => {
        return new Intl.NumberFormat('es-ES', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      };

      // Buscar las líneas por concepto
      const shippingLine = response.lines.find((l) => l.concept === 'SHIPPING');
      const preparationLine = response.lines.find((l) => l.concept === 'PREPARATION');
      const codLine = response.lines.find((l) => l.concept === 'COD');

      // Actualizar valores del formulario
      setFormValues((prev) => ({
        ...prev,
        shippingPrice: shippingLine ? formatPrice(shippingLine.base_amount) : prev.shippingPrice,
        preparationPrice: preparationLine ? formatPrice(preparationLine.base_amount) : prev.preparationPrice,
        codPrice: codLine ? formatPrice(codLine.base_amount) : prev.codPrice,
      }));

      // Actualizar el total en modalData
      const formattedTotal = formatCurrencyAmount(response.summary.total_base_amount, currency);

      setModalData((prev) => ({
        ...prev,
        totalPrice: formattedTotal,
      }));
    }
  }, [modalData.internalId, formValues.orderWeight, formValues.numberOfLines, calculateLines, currency, t, toast]);

  // 🎯 Recalcular precio total cuando cambien los precios del formulario
  useEffect(() => {
    const shipping = parseFloat(formValues.shippingPrice.replace(',', '.')) || 0;
    const preparation = parseFloat(formValues.preparationPrice.replace(',', '.')) || 0;
    const cod = parseFloat(formValues.codPrice.replace(',', '.')) || 0;
    const total = shipping + preparation + cod;

    const formattedTotal = formatCurrencyAmount(total, currency);

    setModalData((prev) => ({
      ...prev,
      totalPrice: formattedTotal,
    }));
  }, [formValues.shippingPrice, formValues.preparationPrice, formValues.codPrice, currency]);

  return (
    <>
      <ModalCenter
        ref={modalRef}
        title={t('modals.editar_prefactura.title', 'Editar pedido')}
        type="default"
        width="xxl"
        onCloseModal={handleCloseAttempt}
        data-testid="editar-prefactura-modal"
      >
        <div className="max-h-[calc(100vh-8rem)] overflow-y-auto">
          {/* Diseño de dos columnas: información (izq) y formulario (der) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Columna izquierda: Información de solo lectura */}
            <div className="space-y-4">
              {/* Información general */}
              <SectionBox className="bg-card-bg">
                <div className="flex items-center gap-2 mb-4">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-icons w-4 h-4" />
                  <h3 className="font-medium text-title">
                    {t('modals.editar_prefactura.general.title', 'Información general')}
                  </h3>
                </div>
                <div className="space-y-3">
                  <ReadOnlyField
                    label={t('modals.editar_prefactura.general.reference', 'Referencia')}
                    value={modalData.reference}
                  />
                  <ReadOnlyField label={t('modals.editar_prefactura.general.shop', 'Tienda')} value={modalData.shop} />
                  <ReadOnlyField
                    label={t('modals.editar_prefactura.general.client', 'Cliente')}
                    value={modalData.client}
                  />
                  <ReadOnlyField
                    label={t('modals.editar_prefactura.general.payment_method', 'Método de pago')}
                    value={modalData.paymentMethod}
                  />
                  <ReadOnlyField
                    label={t('modals.editar_prefactura.general.internal_id', 'ID Interno')}
                    value={modalData.internalId}
                  />
                </div>
              </SectionBox>

              {/* Información logística */}
              <SectionBox className="bg-card-bg">
                <div className="flex items-center gap-2 mb-4">
                  <FontAwesomeIcon icon={faTruckFast} className="text-icons w-4 h-4" />
                  <h3 className="font-medium text-title">
                    {t('modals.editar_prefactura.logistica.title', 'Detalles logísticos')}
                  </h3>
                </div>
                <div className="space-y-3">
                  <ReadOnlyField label={t('modals.editar_prefactura.logistica.type', 'Tipo')} value={modalData.type} />
                  <ReadOnlyField
                    label={t('modals.editar_prefactura.logistica.logistic_status', 'Estado logístico')}
                    value={modalData.logisticStatus}
                  />
                  <ReadOnlyField
                    label={t('modals.editar_prefactura.logistica.shipping_or_return', 'Tipo de envío')}
                    value={modalData.shippingOrReturn}
                  />
                  <ReadOnlyField
                    label={t('modals.editar_prefactura.logistica.courier', 'Courier')}
                    value={modalData.courier}
                  />
                </div>
              </SectionBox>

              {/* Origen y Destino */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Origen */}
                <SectionBox className="bg-card-bg">
                  <div className="flex items-center gap-2 mb-4">
                    <MovimientosIcon className="text-icons w-4 h-4" />
                    <h4 className="font-medium text-title">
                      {t('modals.editar_prefactura.logistica.origin', 'Origen')}
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {modalData.originCountry && (
                      <>
                        {modalData.originCountry === 'Beeping' ? (
                          <div className="text-sm text-text font-medium">Beeping</div>
                        ) : (
                          <>
                            <ReadOnlyField
                              label={t('modals.editar_prefactura.logistica.country', 'País')}
                              value={modalData.originCountry}
                            />
                            {modalData.originMunicipality && (
                              <ReadOnlyField
                                label={t('modals.editar_prefactura.logistica.municipality', 'Municipio')}
                                value={modalData.originMunicipality}
                              />
                            )}
                            {modalData.originPostalCode && (
                              <ReadOnlyField
                                label={t('modals.editar_prefactura.logistica.postal_code', 'Cód. Postal')}
                                value={modalData.originPostalCode}
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </SectionBox>

                {/* Destino */}
                <SectionBox className="bg-card-bg">
                  <div className="flex items-center gap-2 mb-4">
                    <FontAwesomeIcon icon={faLocationDot} className="text-icons w-4 h-4" />
                    <h4 className="font-medium text-title">
                      {t('modals.editar_prefactura.logistica.destination', 'Destino')}
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {modalData.destinationCountry && (
                      <>
                        {modalData.destinationCountry === 'Beeping' ? (
                          <div className="text-sm text-text font-medium">Beeping</div>
                        ) : (
                          <>
                            <ReadOnlyField
                              label={t('modals.editar_prefactura.logistica.country', 'País')}
                              value={modalData.destinationCountry}
                            />
                            {modalData.destinationMunicipality && (
                              <ReadOnlyField
                                label={t('modals.editar_prefactura.logistica.municipality', 'Municipio')}
                                value={modalData.destinationMunicipality}
                              />
                            )}
                            {modalData.destinationPostalCode && (
                              <ReadOnlyField
                                label={t('modals.editar_prefactura.logistica.postal_code', 'Cód. Postal')}
                                value={modalData.destinationPostalCode}
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </SectionBox>
              </div>
            </div>

            {/* Columna derecha: Formulario editable */}
            <div className="space-y-4">
              <SectionBox className="bg-card-bg">
                <div className="flex items-center gap-2 mb-4">
                  <FontAwesomeIcon icon={faFileInvoice} className="text-icons w-4 h-4" />
                  <h3 className="font-medium text-title">
                    {t('modals.editar_prefactura.general.edit_prices', 'Editar precios')}
                  </h3>
                </div>
                <div className="space-y-4">
                  <FormField
                    label={
                      t('modals.editar_prefactura.general.shipping_price', 'Precio envío') +
                      ` (${getCurrencySymbol(currency)})`
                    }
                    value={formValues.shippingPrice}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, shippingPrice: value }))}
                    placeholder="0,00"
                    sanitize
                  />
                  <FormField
                    label={
                      t('modals.editar_prefactura.general.preparation_price', 'Precio preparación') +
                      ` (${getCurrencySymbol(currency)})`
                    }
                    value={formValues.preparationPrice}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, preparationPrice: value }))}
                    placeholder="0,00"
                    sanitize
                  />
                  <FormField
                    label={
                      t('modals.editar_prefactura.general.cod_price', 'Precio COD') +
                      ` (${getCurrencySymbol(currency)})`
                    }
                    value={formValues.codPrice}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, codPrice: value }))}
                    placeholder="0,00"
                    sanitize
                  />
                  <div className="space-y-2">
                    <FormField
                      label={t('modals.editar_prefactura.logistica.order_weight', 'Peso del pedido') + ' (kg)'}
                      value={formValues.orderWeight}
                      onChange={(value) => setFormValues((prev) => ({ ...prev, orderWeight: value }))}
                      placeholder="0,00"
                      sanitize
                    />
                    <FormField
                      label={t('modals.editar_prefactura.logistica.number_of_lines', 'N° de líneas')}
                      value={formValues.numberOfLines}
                      onChange={(value) => setFormValues((prev) => ({ ...prev, numberOfLines: value }))}
                      placeholder="0"
                    />
                    <Button
                      variant="secondary"
                      onClick={handleRecalculate}
                      disabled={isCalculating || !formValues.orderWeight || !formValues.numberOfLines}
                      className="w-full"
                      data-testid="recalculate-lines-button"
                    >
                      <FontAwesomeIcon icon={faCalculator} className="mr-2" />
                      {isCalculating
                        ? t('modals.editar_prefactura.recalculating', 'Recalculando...')
                        : t('modals.editar_prefactura.recalculate', 'Recalcular importes')}
                    </Button>
                  </div>
                </div>
              </SectionBox>

              {/* Total */}
              <SectionBox className="bg-card-bg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text font-medium">
                    {t('modals.editar_prefactura.general.total_price', 'Precio total')}:
                  </span>
                  <span className="text-lg text-success font-semibold">{modalData.totalPrice}</span>
                </div>
              </SectionBox>

              {/* Botón guardar */}
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={!hasChanges}
                  data-testid="editar-prefactura-save"
                >
                  {t('modals.editar_prefactura.button', 'Guardar')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModalCenter>

      {/* Modal de confirmación para cambios sin guardar */}
      <ConfirmModal ref={confirmModalRef} />
    </>
  );
});

EditarPrefacturaModal.displayName = 'EditarPrefacturaModal';
